import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { useBehaviorTracker } from 'contexts/behaviors';
import { NoneOption } from 'rangeConfig/@types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import Box from 'ui/Box';
import Body from 'ui/Typography/Body';
import { imageHoverTransition } from 'utils/imageUtils';
import { animated, useSpring } from 'react-spring';
import { useCore } from 'contexts/core';
import CheckboxIcon from './CheckboxIcon';
import { useSelector } from 'react-redux';
import { getProductsInScene } from 'ducks/products';
import { isMobile } from 'utils/platform';
import NoneOptionImage from './NoneOptionImage';

const StyledCard = styled.div`
  position: relative;
  background: #fff;
  cursor: pointer;
  width: 100%;
  border: none;
`;

const StyledButton = styled.button`
  position: relative;
  background: #fff;
  cursor: pointer;
  width: 100%;
  border: none;
  transition: ${imageHoverTransition()};
  box-shadow: 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0);

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 8px 8px rgba(0, 0, 0, 0.04);
    }
  }
  &:focus {
    outline: 0.125rem solid ${({ theme }) => theme.colors.feedback.focus};
  }

  &[data-is-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StyledHeader = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: block;
  z-index: 9;
  padding: ${({ theme }) => theme.space.xs};
`;

const StyledBox = styled(Box)`
  text-align: left;
`;

const StyledTitle = styled(Body).attrs({
  fontWeight: 'bold'
})`
  line-height: 1;
`;

const StyledSubtitle = styled(Body)`
  line-height: 1;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const Image = styled.img`
  width: 100%;
`;

interface Props {
  noneOption: NoneOption | undefined;
  currentMenu: string;
}

const NoneOptionCard = React.memo(({ noneOption }: Props) => {
  const { formatMessage } = useIntl();
  const [count, setCount] = useState<number>(0);
  const behaviorTracker = useBehaviorTracker();
  const coreApi = useCore();
  const productsInScene = useSelector(getProductsInScene);

  const scaleCounterProps = useSpring({
    opacity: count > 0 ? 1 : 0,
    scale: count > 0 ? 1 : 0,
    from: { opacity: 0, scale: 0 }
  });

  useEffect(() => {
    const count = noneOption?.selected(coreApi) ? 1 : 0;
    setCount(count);
  }, [noneOption, coreApi, productsInScene]);

  if (!noneOption) return null;

  const onClick = () => {
    behaviorTracker.trigger('removeProducts', null, noneOption.behaviors);
  };

  return (
    <StyledCard>
      <StyledButton
        data-is-disabled={false}
        data-testid="none-option"
        onClick={onClick}
        aria-label={formatMessage({ id: noneOption.title })}
      >
        <StyledHeader>
          <animated.div style={scaleCounterProps}>
            <CheckboxIcon />
          </animated.div>
        </StyledHeader>
        {noneOption.image ? (
          <Image
            src={noneOption.image}
            style={{
              border:
                isMobile() && noneOption.selected(coreApi)
                  ? '1px solid black'
                  : '1px solid transparent'
            }}
          />
        ) : (
          <NoneOptionImage />
        )}

        <StyledBox p="xs">
          <StyledTitle type={ThemeFontStyleTypeEnum.bodySmall}>
            {formatMessage({ id: noneOption.title })}
          </StyledTitle>
          {noneOption.subtitle && (
            <StyledSubtitle type={ThemeFontStyleTypeEnum.bodySmall}>
              {formatMessage({ id: noneOption.subtitle })}
            </StyledSubtitle>
          )}
        </StyledBox>
      </StyledButton>
    </StyledCard>
  );
});

export default NoneOptionCard;
